import { useEffect, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import useAuth from "@/hooks/useAuth";
import { updateCookingTimeSettings } from "@/api/settings/take-order-settings";
import toast from "react-hot-toast";

const daySchema = z.object({
  day_id: z.number(),
  minutes: z.coerce.number().min(0, "Minutes must be positive"),
  is_active: z.boolean(),
});

const formSchema = z.object({
  client_id: z.number().optional(),
  is_cooking_time_enabled: z.boolean(),
  days: z.array(daySchema),
});

const DAYS = {
  
  1: "Monday",
  2: "Tuesday",
  3: "Wednesday",
  4: "Thursday",
  5: "Friday",
  6: "Saturday",
  7: "Sunday",
};

function CookingTimeTab() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      is_cooking_time_enabled: true,
      days: [
        { day_id: 1, minutes: 30, is_active: true },
        { day_id: 2, minutes: 30, is_active: true },
        { day_id: 3, minutes: 30, is_active: true },
        { day_id: 4, minutes: 30, is_active: true },
        { day_id: 5, minutes: 30, is_active: true },
        { day_id: 6, minutes: 30, is_active: true },
        { day_id: 7, minutes: 30, is_active: true },
      ],
    },
  });

  const { fields } = useFieldArray({
    control: form.control,
    name: "days",
  });

  useEffect(() => {
    const settings = localStorage.getItem("settings");
    if (!settings) {
      return;
    }

    const settingsData = JSON.parse(settings);

    if (settingsData && settingsData?.take_order && settingsData?.take_order?.cooking_time_setting) {
      console.log("Loading cooking time settings from localStorage");
      const res = settingsData.take_order.cooking_time_setting;
      console.log(res);
      form.setValue("is_cooking_time_enabled", res.is_cooking_time_enabled);
      form.setValue("days", res.days);
      //   form.reset({
      //     client_id: res.client_id,
      //     is_cooking_time_enabled: res.is_cooking_time_enabled,
      //     days: res.days,
      //   });
    }

  }, []);

  const onSubmit = async (data) => {
    setLoading(true);
    const res = await updateCookingTimeSettings(user?.restaurant_id, data);
    if (res.status == "success") { 
      toast.success("Cooking time settings updated successfully");

      localStorage.setItem("settings", JSON.stringify({
        ...JSON.parse(localStorage.getItem("settings") || "{}"),
        take_order: {
          ...JSON.parse(localStorage.getItem("settings") || "{}").take_order,
          cooking_time_setting: data,
        },
      }));

    } else {
      toast.error("Failed to update settings");
    }
    setLoading(false);
  };

  return (
    <TabsContent value="cooking-time">
      <div className="w-full p-4 bg-neutral-900 text-white rounded-md border border-neutral-800">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            
            {/* Global Switch */}
            {/* <FormField
              control={form.control}
              name="is_cooking_time_enabled"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg p-2">
                  <FormLabel className="text-base font-medium">
                    Average Cooking Time
                  </FormLabel>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            /> */}

            <div className="space-y-4">
              {fields.map((field, index) => (
                <div
                  key={field.id}
                  className="flex items-center justify-between p-2 rounded-md hover:bg-neutral-800/50 transition-colors"
                >
                  <div className="flex items-center space-x-4">
                    {/* Day Active Checkbox */}
                    <FormField
                      control={form.control}
                      name={`days.${index}.is_active`}
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                            //   onCheckedChange={field.onChange}
                              className="border-white/50 data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    
                    {/* Day Name */}
                    <span className="text-sm font-medium">
                      {DAYS[form.getValues(`days.${index}.day_id`)] || `Day ${form.getValues(`days.${index}.day_id`)}`}
                    </span>
                  </div>

                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-neutral-400">Time</span>
                    
                    {/* Minutes Input */}
                    <FormField
                      control={form.control}
                      name={`days.${index}.minutes`}
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Input
                              type="number"
                              {...field}
                              className="w-16 h-8 text-center bg-neutral-800 border-neutral-700 text-white focus:ring-green-500 focus:border-green-500"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <span className="text-sm text-neutral-400">Min</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end pt-4">
              <Button 
                type="submit" 
                disabled={loading}
                className="bg-green-500 hover:bg-green-600 text-white min-w-[100px]"
              >
                {loading ? "Saving..." : "Save"}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </TabsContent>
  );
}

export default CookingTimeTab;
